// จัดการรายได้แพทย์ — helpers for the monthly doctor DF summary.
// Backend: Xano `salary_doctor` (one row per doctor per month), read via
// /api/salary-doctor, produced by /api/salary-doctor/gen (df_doctor/gen2).

export { fmtMoney, TH_MONTHS } from './expense.js'

/* ── the money model (from the df_doctor/gen2 XanoScript) ─────────────────────
 *
 * Per doctor per month, aggregated from `billing` joined to `treatment` and
 * `payment_method`, counting only billing lines with a non-zero `priceDoctor`:
 *
 *   total       Σ priceXunit    what the patients paid
 *   df          Σ priceDoctor   the doctor's fee out of that
 *   totaltrace  the `total` half whose payment_method is TRACEABLE (in the books)
 *   df_trace / df_nontrace      the same split applied to the fee
 *   lab         Σ priceDoctor of that doctor's labs at labstage ≥ 7 this month
 *   transfer    what actually gets paid out of the non-traceable pocket
 *
 * `lab` is charged to the NON-TRACEABLE fee first and only then eats into the
 * traceable one — a three-branch waterfall:
 *
 *   lab ≥ df            → df_trace = 0, df_nontrace = 0, transfer = df − lab
 *                         (negative: the doctor owes the clinic)
 *   lab ≥ df_nontrace   → df_nontrace = 0,
 *                         df_trace = df_trace + (df_nontrace − lab)   [reduced],
 *                         transfer = 0
 *   otherwise           → transfer = df_nontrace − lab
 *
 * ⚠️ The three branches leave the stored columns in DIFFERENT states, so no
 * simple "df_trace + df_nontrace" reading is safe: `df_nontrace` is the ORIGINAL
 * figure in branch 3 but forced to 0 in branches 1-2, and `df_trace` is reduced
 * only in branch 2. The one identity that holds in EVERY branch is:
 *
 *        df_trace  +  lab  +  transfer  ==  df
 *
 * (verified across all 203 rows of 2026-06 and all 208 of 2026-07). Read it as:
 * the fee splits into what is paid in the books, what the lab ate, and what is
 * paid outside. `checkBalance()` below is that identity, and the payout the
 * clinic actually makes is `df_trace + transfer` = `df − lab`.
 *
 * Payout is TWO bank transfers (see df_doctor/export2):
 *   traceable      amount = df_trace,  to bank_track
 *   non-traceable  amount = transfer,  to bank_nontrack (falls back to bank_track)
 * That is what the doctor record's two accounts are for.                       */

export const n = (v) => Number(v || 0)

export const rowTotal = (r) => n(r?.total)
export const rowDF = (r) => n(r?.df)
export const rowLab = (r) => n(r?.lab)
export const rowTrace = (r) => n(r?.df_trace)
export const rowNonTrace = (r) => n(r?.df_nontrace)
export const rowTransfer = (r) => n(r?.transfer)
// What leaves the clinic for this doctor: the traceable leg plus the
// non-traceable one. `transfer` alone under-reports whenever lab ate into
// df_trace, and df alone over-reports by the lab deduction.
export const rowPayout = (r) => rowTrace(r) + Math.max(0, rowTransfer(r))
// Negative transfer = lab exceeded the whole fee, so the doctor owes it back.
export const rowOwed = (r) => (rowTransfer(r) < 0 ? -rowTransfer(r) : 0)

// The cross-foot: df_trace + lab + transfer must equal df. A row that fails it
// was not produced by the gen2 waterfall — someone edited a figure by hand, or a
// run half-completed — so the page shows it rather than quietly totalling it in.
// 0.011 tolerance because the figures carry satang and float sums drift.
export function checkBalance(r) {
  const lhs = rowTrace(r) + rowLab(r) + rowTransfer(r)
  const diff = lhs - rowDF(r)
  return { ok: Math.abs(diff) <= 0.011, diff }
}

// `live` is set by df_doctor/labstage (the Lab page's หักยอด action) — the month
// is settled and its labs are locked at stage 8.
export const isSettled = (r) => r?.live === true

export function settledInfo(settled) {
  return settled
    ? { label: 'หักยอดแล้ว', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' }
    : { label: 'รอหักยอด', tone: 'bg-amber-50 text-amber-700 ring-amber-200' }
}

// Which of the three waterfall branches a row landed in — the drawer explains
// the arithmetic differently for each, because "df_nontrace = 0" means something
// different when the lab bill was bigger than the whole fee.
export function labBranch(r) {
  const lab = rowLab(r)
  if (!lab) return 'none'
  if (rowTransfer(r) < 0) return 'over'      // lab ≥ df — doctor owes
  if (rowTransfer(r) === 0 && lab > 0) return 'into-trace' // lab ate the non-trace leg
  return 'within'                            // lab covered by the non-trace leg
}

/**
 * ยอดโอนทั้งหมด — the money that actually leaves the bank this month.
 *
 * Rows with a NEGATIVE `transfer` are dropped entirely, then the survivors'
 * `df_trace + transfer` is summed. A negative transfer means the lab bill
 * exceeded the doctor's whole fee, so there is nothing to send them — they owe
 * the clinic, which is collected some other way, not netted off someone else's
 * transfer. Summing without the filter under-reports by exactly those debts.
 *
 * Dropping the whole row is safe rather than merely dropping its negative:
 * `transfer < 0` only occurs in the waterfall branch where `lab >= df`, and that
 * branch forces `df_trace` to 0 — so a discarded row contributes nothing either
 * way. (Verified on 2026-07: all 4 negative rows have df_trace = 0.)
 */
export function payableTotal(rows) {
  return (rows || [])
    .filter((r) => rowTransfer(r) >= 0)
    .reduce((a, r) => a + rowTrace(r) + rowTransfer(r), 0)
}

export const negativeRows = (rows) => (rows || []).filter((r) => rowTransfer(r) < 0)

export function totals(rows) {
  const sum = (f) => (rows || []).reduce((a, r) => a + f(r), 0)
  return {
    doctors: (rows || []).length,
    total: sum(rowTotal),
    df: sum(rowDF),
    trace: sum(rowTrace),
    nonTrace: sum(rowNonTrace),
    lab: sum(rowLab),
    transfer: sum(rowTransfer),
    payout: sum(rowPayout),
    payable: payableTotal(rows),          // ยอดโอนทั้งหมด — see above
    negatives: negativeRows(rows).length,
    settled: (rows || []).filter(isSettled).length,
  }
}

/* ── period ──────────────────────────────────────────────────────────────── */
// Payroll is run for the month that just ended, so that is the default.
export function lastMonth(now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { year: d.getFullYear(), month: d.getMonth() + 1 }
}

// gen2 validates min:2015; the clinic's data starts well after that.
export const SALARY_YEARS = (() => {
  const y = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => y - 3 + i)
})()

export const periodLabel = (year, month, THM) =>
  `${THM[Number(month) - 1] || month} ${Number(year) + 543}`
